import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import type { Article, Deposant, GroupeCommission, Justificatif, ModeReversement } from '@troc/shared'
import {
  adresseComplete,
  eur,
  GROUPES_COMMISSION,
  libelleDemarque,
  LIBELLE_GROUPE_COMMISSION,
  tauxEnPourcent,
} from '@troc/shared'
import { api, ErreurApi } from '../api'
import { Ecran } from '../composants/Ecran'
import { PastilleReversement, PastilleStatut } from '../composants/Pastille'
import { FormulaireClient, depuisDeposant } from '../composants/FormulaireClient'
import { imprimerJustificatif } from '../impression/documents'
import type { SaisieClient } from '../composants/FormulaireClient'
import { useToast } from '../etat/Toast'

interface Versement {
  id: number
  num: string
  periode: string
  libellePeriode: string
  montantCentimes: number
  mode: ModeReversement
  payeLe: string
  vendeurNom: string
}

interface Fiche {
  deposant: Deposant
  articles: Article[]
  /** Tout ce qu'il a déposé — `articles` s'arrête à deux mille. */
  articlesTotal: number
  historique: Versement[]
  totalVerseCentimes: number
  ventes: {
    ref: string
    articleId: number | null
    libelle: string
    prixCentimes: number
    taux: number
    netCentimes: number
    quantite: number
    date: string
  }[]
  reversement: {
    periode: string
    libellePeriode: string
    montantCentimes: number
    nbVentes: number
    soldeCentimes: number
    attenteCentimes: number
    totalDuCentimes: number
    prochaineEcheance: string | null
    mode: ModeReversement
  }
  cumul: { caCentimes: number; commissionCentimes: number; nbVentes: number; tauxMoyen: number }
  performance: Performance
}

interface FamillePerf {
  categorie: string
  deposes: number
  vendus: number
  ecoulement: number
  caCentimes: number
  delaiMoyenJours: number | null
}

interface Performance {
  deposes: number
  vendus: number
  ecoulement: number
  delaiMoyenJours: number | null
  caCentimes: number
  /** Triées du meilleur au moins bon taux d'écoulement. */
  familles: FamillePerf[]
}

const FORMAT_DATE = new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'short' })
const FORMAT_DATE_COMPLETE = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'short' })

function formaterDate(iso: string): string {
  if (!iso) return ''
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? iso : FORMAT_DATE_COMPLETE.format(date)
}

/**
 * Ce que valent les dépôts de ce client, en une phrase.
 *
 * Utile au comptoir : quand quelqu'un revient avec un carton, savoir que ses
 * meubles partent en trois semaines et que ses vinyles dorment depuis six mois
 * change ce qu'on accepte et à quel prix. On ne parle que de ce qui est mesuré
 * — en dessous de trois pièces vendues, on se tait plutôt que d'inventer une
 * tendance sur un coup de chance.
 */
function Analyse({ performance }: { performance: Performance }) {
  const { deposes, vendus, ecoulement, delaiMoyenJours, familles } = performance

  if (deposes === 0) {
    return <div className="analyse-client sourdine">Aucun article déposé pour l’instant.</div>
  }
  if (vendus === 0) {
    return (
      <div className="analyse-client">
        {deposes} article{deposes > 1 ? 's' : ''} déposé{deposes > 1 ? 's' : ''}, aucune vente
        encore.
      </div>
    )
  }

  const pourcent = Math.round(ecoulement * 100)
  const verdict =
    ecoulement >= 0.7
      ? 'Très bon vendeur'
      : ecoulement >= 0.45
        ? 'Bon vendeur'
        : ecoulement >= 0.25
          ? 'Écoulement moyen'
          : 'Écoulement lent'

  // Une famille ne se commente qu'à partir de trois pièces déposées : en
  // dessous, « 1 vendu sur 1 » ne dit rien de la famille, seulement du hasard.
  const parlantes = familles.filter((f) => f.deposes >= 3)

  // Le taux d'écoulement filtre ce qui « se vend bien » / « se vend mal », mais
  // ne doit pas décider seul laquelle citer : 12 meubles vendus sur 12 (100 %)
  // ne pèse rien face à 300 vêtements vendus sur 450 (67 %). Une fois le filtre
  // passé, c'est le volume réellement vendu (ou resté en rayon) qui tranche.
  const fortes = parlantes.filter((f) => f.ecoulement >= 0.6)
  const forte = fortes.length
    ? fortes.reduce((max, f) => (f.vendus > max.vendus ? f : max))
    : null
  const faibles = parlantes.filter((f) => f.ecoulement <= 0.34)
  const faible = faibles.length
    ? faibles.reduce((max, f) => (f.deposes > max.deposes ? f : max))
    : null

  // Top des catégories qui partent le mieux, par volume réellement vendu —
  // les 100 % d'écoulement y ressortent d'eux-mêmes sans qu'on ait besoin
  // d'un classement séparé pour elles.
  const topVentes = [...parlantes].sort((a, b) => b.vendus - a.vendus).slice(0, 5)

  // Même liste triée par chiffre d'affaires : une catégorie qui vend peu de
  // pièces mais chères (ex. horlogerie) ne ressort jamais dans le classement
  // par quantité, alors qu'elle peut peser lourd dans ce que le déposant a
  // effectivement touché.
  const topCA = [...parlantes].sort((a, b) => b.caCentimes - a.caCentimes).slice(0, 5)

  return (
    <div className="analyse-client">
      <strong>{verdict}</strong> — {vendus} vendu{vendus > 1 ? 's' : ''} sur {deposes} déposé
      {deposes > 1 ? 's' : ''} ({pourcent} %)
      {delaiMoyenJours !== null && `, en ${delaiMoyenJours} jours en moyenne`}.
      {forte && (
        <>
          {' '}
          Ce qui part : <strong>{forte.categorie.toLowerCase()}</strong> ({forte.vendus}/
          {forte.deposes}).
        </>
      )}
      {faible && faible.categorie !== forte?.categorie && (
        <>
          {' '}
          Ce qui reste : <strong>{faible.categorie.toLowerCase()}</strong> ({faible.vendus}/
          {faible.deposes}).
        </>
      )}
      {(topVentes.length >= 2 || topCA.length >= 2) && (
        <div className="top-categories-groupe">
          {topVentes.length >= 2 && (
            <div>
              <div className="top-categories-titre">Top ventes (quantité)</div>
              <ol className="top-categories">
                {topVentes.map((f) => (
                  <li key={f.categorie}>
                    <span className="top-categories-nom">{f.categorie.toLowerCase()}</span>
                    {' — '}
                    {f.vendus}/{f.deposes} vendus
                    {f.ecoulement === 1 ? (
                      <span className="pastille pastille-100"> 100 %</span>
                    ) : (
                      ` (${Math.round(f.ecoulement * 100)} %)`
                    )}
                  </li>
                ))}
              </ol>
            </div>
          )}
          {topCA.length >= 2 && (
            <div>
              <div className="top-categories-titre">Top chiffre d’affaires</div>
              <ol className="top-categories">
                {topCA.map((f) => (
                  <li key={f.categorie}>
                    <span className="top-categories-nom">{f.categorie.toLowerCase()}</span>
                    {' — '}
                    {eur(f.caCentimes)}
                    {f.ecoulement === 1 && <span className="pastille pastille-100"> 100 %</span>}
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/** Mention d'identité — signalée en jaune tant qu'elle manque. */
function Mention({ libelle, valeur }: { libelle: string; valeur: string }) {
  return (
    <div>
      <div className="etiquette">{libelle}</div>
      {valeur ? (
        <div style={{ fontSize: 13 }}>{valeur}</div>
      ) : (
        <span className="pastille pastille-a_recuperer">à compléter</span>
      )}
    </div>
  )
}

interface ResumeContrat {
  contratNum: string
  date: string
  nbArticles: number
  enDepot: number
  vendus: number
  totalInitialCentimes: number
}

/**
 * Un déposant qui a fait cinquante dépôts ne se lit pas dans une seule liste
 * de deux mille articles en vrac : on regroupe par contrat, celui du jour même
 * en tête, chacun ouvrant sur le détail déjà connu de l'écran Contrats.
 */
function regrouperParContrat(articles: Article[]): ResumeContrat[] {
  const parContrat = new Map<string, Article[]>()
  for (const article of articles) {
    const liste = parContrat.get(article.contratNum)
    if (liste) liste.push(article)
    else parContrat.set(article.contratNum, [article])
  }
  return [...parContrat.entries()]
    .map(([contratNum, lignes]) => ({
      contratNum,
      date: lignes.reduce((min, a) => (a.deposeLe < min ? a.deposeLe : min), lignes[0]!.deposeLe),
      nbArticles: lignes.length,
      enDepot: lignes.filter((a) => a.statut !== 'vendu' && a.statut !== 'retire').length,
      vendus: lignes.filter((a) => a.statut === 'vendu').length,
      totalInitialCentimes: lignes.reduce((t, a) => t + a.prixInitialCentimes, 0),
    }))
    .sort((a, b) => b.date.localeCompare(a.date))
}

interface VenteGroupee {
  ref: string
  libelle: string
  quantite: number
  prixTotalCentimes: number
  netTotalCentimes: number
  derniereVente: string
}

/**
 * Le même article revendu plusieurs fois — un rouleau de papier abrasif, une
 * paire de lunettes de protection — n'a pas besoin d'une ligne par vente : la
 * quantité dit tout, et la date de la dernière vente suffit à situer.
 */
function regrouperVentes(ventes: Fiche['ventes']): VenteGroupee[] {
  const parRef = new Map<string, VenteGroupee>()
  for (const v of ventes) {
    const existant = parRef.get(v.ref)
    if (existant) {
      existant.quantite += v.quantite
      existant.prixTotalCentimes += v.prixCentimes * v.quantite
      existant.netTotalCentimes += v.netCentimes
      if (v.date > existant.derniereVente) existant.derniereVente = v.date
    } else {
      parRef.set(v.ref, {
        ref: v.ref,
        libelle: v.libelle,
        quantite: v.quantite,
        prixTotalCentimes: v.prixCentimes * v.quantite,
        netTotalCentimes: v.netCentimes,
        derniereVente: v.date,
      })
    }
  }
  return [...parRef.values()].sort((a, b) => b.derniereVente.localeCompare(a.derniereVente))
}

type ColonneVentes =
  | 'derniereVente'
  | 'ref'
  | 'libelle'
  | 'quantite'
  | 'prixUnitaireCentimes'
  | 'prixTotalCentimes'
  | 'netTotalCentimes'

/** Prix unitaire moyen d'une vente groupée : le total ne dit rien du prix à
 * la pièce, or une remise ponctuelle sur une seule vente peut faire varier
 * ce prix d'un regroupement à l'autre. */
function prixUnitaireCentimes(v: VenteGroupee): number {
  return Math.round(v.prixTotalCentimes / v.quantite)
}

const COMPARATEURS_VENTES: Record<ColonneVentes, (v: VenteGroupee) => string | number> = {
  derniereVente: (v) => v.derniereVente,
  ref: (v) => v.ref,
  libelle: (v) => v.libelle,
  quantite: (v) => v.quantite,
  prixUnitaireCentimes: (v) => prixUnitaireCentimes(v),
  prixTotalCentimes: (v) => v.prixTotalCentimes,
  netTotalCentimes: (v) => v.netTotalCentimes,
}

function EnteteTri({
  colonne,
  tri,
  onTrier,
  aDroite,
  children,
}: {
  colonne: ColonneVentes
  tri: { colonne: ColonneVentes; sens: 1 | -1 }
  onTrier: (colonne: ColonneVentes) => void
  aDroite?: boolean
  children: ReactNode
}) {
  const actif = tri.colonne === colonne
  return (
    <th className={aDroite ? 'a-droite' : undefined}>
      <button type="button" className="lien-nu entete-triable" onClick={() => onTrier(colonne)}>
        {children}
        {actif && <span aria-hidden="true"> {tri.sens === 1 ? '▲' : '▼'}</span>}
      </button>
    </th>
  )
}

export function FicheDeposant() {
  const { id } = useParams()
  const naviguer = useNavigate()
  const { signaler, alerter } = useToast()
  const [fiche, setFiche] = useState<Fiche | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [edition, setEdition] = useState<SaisieClient | null>(null)
  const [onglet, setOnglet] = useState<'contrats' | 'articles' | 'ventes' | 'versements'>('contrats')
  // Tri par défaut : dernière vente d'abord, comme le renvoyait déjà regrouperVentes.
  const [triVentes, setTriVentes] = useState<{ colonne: ColonneVentes; sens: 1 | -1 }>({
    colonne: 'derniereVente',
    sens: -1,
  })

  const trierVentesPar = (colonne: ColonneVentes) => {
    setTriVentes((precedent) =>
      precedent.colonne === colonne
        ? { colonne, sens: precedent.sens === 1 ? -1 : 1 }
        // Qté et montants se lisent naturellement du plus grand au plus petit
        // au premier clic ; les colonnes textuelles, de A à Z.
        : { colonne, sens: colonne === 'derniereVente' || colonne === 'ref' || colonne === 'libelle' ? 1 : -1 },
    )
  }

  const charger = useCallback(() => {
    if (!id) return
    api<Fiche>(`/deposants/${id}`)
      .then(setFiche)
      .catch((cause: unknown) =>
        setErreur(cause instanceof ErreurApi ? cause.message : 'Fiche introuvable.'),
      )
  }, [id])

  useEffect(charger, [charger])

  const enregistrer = async () => {
    if (!fiche || !edition) return
    try {
      await api(`/deposants/${fiche.deposant.id}`, { methode: 'PUT', corps: edition })
      signaler('Fiche enregistrée.')
      setEdition(null)
      charger()
    } catch (cause) {
      alerter(cause instanceof ErreurApi ? cause.message : 'Enregistrement impossible.')
    }
  }

  // Changer le groupe de commission n'a rien à voir avec le reste de la fiche
  // (nom, adresse, IBAN...) : forcer à ouvrir tout le formulaire pour ça —
  // comme il fallait le faire jusqu'ici — coûte trop de clics pour un geste
  // fréquent. On envoie la même fiche, avec seulement ce champ changé.
  const changerGroupe = async (groupe: GroupeCommission) => {
    if (!fiche || groupe === fiche.deposant.groupeCommission) return
    try {
      await api(`/deposants/${fiche.deposant.id}`, {
        methode: 'PUT',
        corps: { ...depuisDeposant(fiche.deposant), groupeCommission: groupe },
      })
      signaler(`Groupe de commission changé pour ${groupe}.`)
      charger()
    } catch (cause) {
      alerter(cause instanceof ErreurApi ? cause.message : 'Changement de groupe impossible.')
    }
  }

  const reimprimer = async (reversementId: number) => {
    try {
      const reponse = await api<{ justificatif: Justificatif }>(`/justificatifs/${reversementId}`)
      imprimerJustificatif(reponse.justificatif)
    } catch (cause) {
      alerter(cause instanceof ErreurApi ? cause.message : 'Justificatif introuvable.')
    }
  }

  // Calculé avant tout retour conditionnel : un Hook ne peut pas se poser
  // après un `if` qui renvoie, sous peine de désynchroniser React d'un rendu
  // à l'autre (ici entre l'écran de chargement et la fiche chargée).
  const contrats = useMemo(() => regrouperParContrat(fiche?.articles ?? []), [fiche])
  const ventesGroupees = useMemo(() => regrouperVentes(fiche?.ventes ?? []), [fiche])
  const ventesTriees = useMemo(() => {
    const cle = COMPARATEURS_VENTES[triVentes.colonne]
    return [...ventesGroupees].sort((a, b) => {
      const va = cle(a)
      const vb = cle(b)
      const ecart = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb))
      return ecart * triVentes.sens
    })
  }, [ventesGroupees, triVentes])

  if (erreur) {
    return (
      <Ecran titre="Fiche client" sousTitre="Introuvable">
        <div className="vide">{erreur}</div>
      </Ecran>
    )
  }

  if (!fiche) {
    return (
      <Ecran titre="Fiche client" sousTitre="Chargement…">
        <div className="vide">Chargement…</div>
      </Ecran>
    )
  }

  const { deposant, articles, articlesTotal, ventes, reversement, cumul, performance } = fiche
  const enDepot = articles.filter((a) => a.statut !== 'vendu' && a.statut !== 'retire')
  const aRecuperer = enDepot.filter((a) => a.statut === 'a_recuperer').length
  const initiales = deposant.nom
    .split(/\s+/)
    .slice(0, 2)
    .map((mot) => mot.charAt(0).toUpperCase())
    .join('')

  return (
    <Ecran titre="Fiche client" sousTitre="Contrats, articles, reversement">
      <div className="zone-defilante" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
          <div
            style={{
              width: 88,
              height: 88,
              flex: 'none',
              background: 'var(--color-accent)',
              color: 'var(--color-bg)',
              display: 'grid',
              placeItems: 'center',
              fontFamily: 'var(--font-heading)',
              fontWeight: 800,
              fontSize: 30,
            }}
          >
            {initiales}
          </div>
          <div style={{ flex: 1 }}>
            <div className="ref">
              CLIENT {deposant.ref} · DEPUIS{' '}
              {new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' })
                .format(new Date(deposant.creeLe))
                .toUpperCase()}
            </div>
            <h2 style={{ margin: '2px 0 4px', fontSize: 34 }}>
              {deposant.titre ? `${deposant.titre} ` : ''}
              {deposant.nom}
            </h2>
            <div className="sourdine" style={{ fontSize: 13 }}>
              {[deposant.gsm, deposant.tel, deposant.email, adresseComplete(deposant)]
                .filter(Boolean)
                .join(' · ') || '—'}
            </div>
            <Analyse performance={performance} />
            <div style={{ display: 'flex', gap: 20, marginTop: 8, flexWrap: 'wrap' }}>
              <Mention libelle="Naissance" valeur={formaterDate(deposant.dateNaissance)} />
              <Mention libelle="Carte d’identité" valeur={deposant.numIdentite} />
              <Mention libelle="IBAN" valeur={deposant.iban} />
              <div>
                <div className="etiquette">Commission</div>
                <div style={{ fontSize: 13 }} title={LIBELLE_GROUPE_COMMISSION[deposant.groupeCommission]}>
                  Groupe {deposant.groupeCommission}
                </div>
              </div>
            </div>
            {deposant.notes && (
              <div className="note" style={{ marginTop: 10, maxWidth: 620 }}>
                {deposant.notes}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: 180, flexShrink: 0 }}>
            <button
              type="button"
              className="btn btn-primaire"
              onClick={() =>
                // On emporte le client : l'écran de dépôt ouvre directement la
                // saisie des articles, sans refaire choisir la même personne.
                naviguer('/depot', { state: { deposant } })
              }
            >
              Nouveau dépôt
            </button>
            <button
              type="button"
              className="btn btn-secondaire"
              onClick={() => setEdition(depuisDeposant(deposant))}
            >
              Modifier la fiche
            </button>
            <select
              className="saisie"
              style={{ fontSize: 12 }}
              value={deposant.groupeCommission}
              title={LIBELLE_GROUPE_COMMISSION[deposant.groupeCommission]}
              onChange={(e) => void changerGroupe(e.target.value as GroupeCommission)}
            >
              {GROUPES_COMMISSION.map((groupe) => (
                <option key={groupe} value={groupe}>
                  Commission — Groupe {groupe}
                </option>
              ))}
            </select>
          </div>
        </div>

        {edition && (
          <div
            style={{
              border: '2px solid var(--color-text)',
              padding: 24,
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            <h4 style={{ margin: 0 }}>Modifier la fiche client</h4>
            <FormulaireClient valeur={edition} onChange={setEdition} />
            <div style={{ display: 'flex', gap: 10, maxWidth: 780 }}>
              <button
                type="button"
                className="btn btn-secondaire btn-large"
                onClick={() => setEdition(null)}
              >
                Annuler
              </button>
              <button
                type="button"
                className="btn btn-primaire btn-large"
                onClick={() => void enregistrer()}
              >
                Enregistrer
              </button>
            </div>
          </div>
        )}

        <div className="bandeau-kpi">
          <div className="kpi kpi-vedette">
            <div className="etiquette">À remettre</div>
            <div className="valeur">{eur(reversement.soldeCentimes)}</div>
            <div className="detail">
              {reversement.attenteCentimes > 0
                ? `+ ${eur(reversement.attenteCentimes)} pas encore échus`
                : reversement.soldeCentimes > 0
                  ? 'payable aujourd’hui'
                  : 'rien en attente'}
            </div>
          </div>
          <div className="kpi">
            <div className="etiquette">Ventes cumulées</div>
            <div className="valeur">{eur(cumul.caCentimes)}</div>
            <div className="detail">{cumul.nbVentes} articles vendus</div>
          </div>
          <div className="kpi">
            <div className="etiquette">En dépôt</div>
            <div className="valeur">{enDepot.length}</div>
            <div className="detail">
              {aRecuperer > 0 ? `dont ${aRecuperer} à récupérer` : 'aucun à récupérer'}
            </div>
          </div>
          <div className="kpi">
            <div className="etiquette">Commission moy.</div>
            <div className="valeur">{tauxEnPourcent(cumul.tauxMoyen)}</div>
            <div className="detail">sur l’ensemble des ventes</div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="seg" style={{ marginBottom: 12 }}>
              {(
                [
                  ['contrats', `Contrats (${contrats.length})`],
                  [
                    'articles',
                    articlesTotal > articles.length
                      ? `Tous les articles (${articles.length} sur ${articlesTotal})`
                      : `Tous les articles (${articles.length})`,
                  ],
                  ['ventes', `Ventes (${ventes.length})`],
                  ['versements', `Versements (${fiche.historique.length})`],
                ] as const
              ).map(([cle, libelle]) => (
                <button
                  key={cle}
                  type="button"
                  className="seg-opt"
                  aria-pressed={onglet === cle}
                  onClick={() => setOnglet(cle)}
                >
                  {libelle}
                </button>
              ))}
            </div>

            {onglet === 'contrats' && (
              <>
                <table className="tableau">
                  <thead>
                    <tr>
                      <th>Contrat</th>
                      <th>Date</th>
                      <th className="a-droite">Articles</th>
                      <th className="a-droite">En dépôt</th>
                      <th className="a-droite">Vendus</th>
                      <th className="a-droite">Total initial</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contrats.map((contrat) => (
                      <tr key={contrat.contratNum}>
                        <td>
                          <Link to={`/contrats/${contrat.contratNum}`} className="ref">
                            {contrat.contratNum}
                          </Link>
                        </td>
                        <td>{formaterDate(contrat.date)}</td>
                        <td className="a-droite">{contrat.nbArticles}</td>
                        <td className="a-droite">{contrat.enDepot}</td>
                        <td className="a-droite">{contrat.vendus}</td>
                        <td className="a-droite chiffre">{eur(contrat.totalInitialCentimes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {contrats.length === 0 && <div className="vide">Aucun contrat de dépôt.</div>}
                {articlesTotal > articles.length && (
                  <div className="note" style={{ marginTop: 10 }}>
                    {articles.length} des {articlesTotal} articles sont chargés — les contrats les
                    plus anciens peuvent manquer ici.
                  </div>
                )}
              </>
            )}

            {onglet === 'articles' && (
              <>
                <table className="tableau">
                  <thead>
                    <tr>
                      <th>Réf.</th>
                      <th>Désignation</th>
                      <th>Contrat</th>
                      <th>Mois</th>
                      <th className="a-droite">Prix initial</th>
                      <th className="a-droite">Prix actuel</th>
                      <th>Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {articles.map((article) => (
                      <tr key={article.id}>
                        <td className="ref">{article.ref}</td>
                        <td className="titre">{article.titre}</td>
                        <td>
                          <Link to={`/contrats/${article.contratNum}`} className="ref">
                            {article.contratNum}
                          </Link>
                        </td>
                        <td>{libelleDemarque(article.mois)}</td>
                        <td className="a-droite barre">
                          {article.decote > 0 ? eur(article.prixInitialCentimes) : '—'}
                        </td>
                        <td className="a-droite chiffre">{eur(article.prixActuelCentimes)}</td>
                        <td>
                          <PastilleStatut statut={article.statut} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {articles.length === 0 && <div className="vide">Aucun article déposé.</div>}
              </>
            )}

            {onglet === 'ventes' && (
              <>
                <table className="tableau">
                  <thead>
                    <tr>
                      <EnteteTri colonne="derniereVente" tri={triVentes} onTrier={trierVentesPar}>Dernière vente</EnteteTri>
                      <EnteteTri colonne="ref" tri={triVentes} onTrier={trierVentesPar}>Réf.</EnteteTri>
                      <EnteteTri colonne="libelle" tri={triVentes} onTrier={trierVentesPar}>Désignation</EnteteTri>
                      <EnteteTri colonne="quantite" tri={triVentes} onTrier={trierVentesPar} aDroite>Qté</EnteteTri>
                      <EnteteTri colonne="prixUnitaireCentimes" tri={triVentes} onTrier={trierVentesPar} aDroite>Px. unitaire</EnteteTri>
                      <EnteteTri colonne="prixTotalCentimes" tri={triVentes} onTrier={trierVentesPar} aDroite>Px. vente</EnteteTri>
                      <th className="a-droite">Commission</th>
                      <EnteteTri colonne="netTotalCentimes" tri={triVentes} onTrier={trierVentesPar} aDroite>PV. déposant</EnteteTri>
                    </tr>
                  </thead>
                  <tbody>
                    {ventesTriees.map((vente) => (
                      <tr key={vente.ref}>
                        <td>{FORMAT_DATE.format(new Date(vente.derniereVente))}</td>
                        <td className="ref">{vente.ref}</td>
                        <td className="titre">{vente.libelle}</td>
                        <td className="a-droite chiffre">{vente.quantite}</td>
                        <td className="a-droite sourdine">{eur(prixUnitaireCentimes(vente))}</td>
                        <td className="a-droite chiffre">{eur(vente.prixTotalCentimes)}</td>
                        <td className="a-droite sourdine">
                          {eur(vente.prixTotalCentimes - vente.netTotalCentimes)}
                        </td>
                        <td className="a-droite chiffre">{eur(vente.netTotalCentimes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {ventes.length === 0 && <div className="vide">Aucune vente enregistrée.</div>}
              </>
            )}

            {onglet === 'versements' && (
              <>
                <table className="tableau">
                  <thead>
                    <tr>
                      <th>Payé le</th>
                      <th>N° justificatif</th>
                      <th>Période</th>
                      <th>Mode</th>
                      <th>Responsable</th>
                      <th className="a-droite">Montant</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {fiche.historique.map((versement) => (
                      <tr key={versement.id}>
                        <td>{formaterDate(versement.payeLe)}</td>
                        <td className="ref">{versement.num || '—'}</td>
                        <td className="titre">{versement.libellePeriode}</td>
                        <td>
                          <PastilleReversement mode={versement.mode} />
                        </td>
                        <td>{versement.vendeurNom}</td>
                        <td className="a-droite chiffre">{eur(versement.montantCentimes)}</td>
                        <td className="a-droite">
                          <button
                            type="button"
                            className="btn btn-fantome"
                            onClick={() => void reimprimer(versement.id)}
                          >
                            Réimprimer
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {fiche.historique.length === 0 ? (
                  <div className="vide">Aucun versement effectué à ce jour.</div>
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '12px 8px',
                      borderTop: '2px solid var(--color-divider)',
                      alignItems: 'baseline',
                    }}
                  >
                    <span className="titre">TOTAL VERSÉ DEPUIS L’OUVERTURE</span>
                    <span className="chiffre" style={{ fontSize: 24 }}>
                      {eur(fiche.totalVerseCentimes)}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>

          <div
            style={{
              width: 320,
              flex: 'none',
              background: 'var(--color-accent-2-100)',
              borderLeft: '4px solid var(--color-accent-2-400)',
              padding: '18px 18px 18px 20px',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            <div className="etiquette">À remettre aujourd’hui</div>
            <div className="chiffre" style={{ fontSize: 40, lineHeight: 1, color: 'var(--color-accent)' }}>
              {eur(reversement.soldeCentimes)}
            </div>
            <div className="sourdine" style={{ fontSize: 12 }}>
              {reversement.soldeCentimes > 0
                ? `Règlement en ${reversement.mode === 'virement' ? 'virement bancaire (au-delà de 3 000 €)' : 'espèces'}.`
                : 'Rien à remettre pour le moment.'}
            </div>
            {reversement.soldeCentimes > 0 && (
              <div>
                <PastilleReversement mode={reversement.mode} />
              </div>
            )}

            {reversement.attenteCentimes > 0 && (
              <div
                style={{
                  background: 'var(--color-attention-100)',
                  color: 'var(--color-attention-700)',
                  padding: '10px 12px',
                }}
              >
                <div className="etiquette" style={{ color: 'inherit' }}>
                  Pas encore échu
                </div>
                <div className="chiffre" style={{ fontSize: 22 }}>
                  {eur(reversement.attenteCentimes)}
                </div>
                <div style={{ fontSize: 11 }}>
                  Ventes trop récentes — payables{' '}
                  {reversement.prochaineEcheance
                    ? `à partir du ${formaterDate(reversement.prochaineEcheance)}`
                    : 'à la prochaine échéance'}
                  .
                </div>
              </div>
            )}

            <hr className="filet" style={{ margin: '6px 0' }} />

            {ventes.slice(0, 8).map((vente, index) => {
              const contenu = (
                <>
                  <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {FORMAT_DATE.format(new Date(vente.date))} ·{' '}
                    <span className="ref">{vente.ref}</span> · {vente.libelle}
                  </span>
                  <span className="titre">{eur(vente.netCentimes)}</span>
                </>
              )
              const style = {
                display: 'flex',
                justifyContent: 'space-between',
                gap: 8,
                fontSize: 12,
                paddingBottom: 6,
                borderBottom: '1px solid var(--color-hairline)',
              } as const
              return vente.articleId ? (
                <Link
                  key={`${vente.ref}-${index}`}
                  to={`/articles/${vente.articleId}`}
                  style={style}
                  title="Voir le détail de l’article vendu"
                >
                  {contenu}
                </Link>
              ) : (
                <div key={`${vente.ref}-${index}`} style={style}>
                  {contenu}
                </div>
              )
            })}
            {ventes.length === 0 && <div className="sourdine" style={{ fontSize: 12 }}>Aucune vente.</div>}

            <button
              type="button"
              className="btn btn-primaire btn-large"
              style={{ marginTop: 6 }}
              disabled={reversement.soldeCentimes <= 0}
              onClick={() => naviguer(`/deposants/${deposant.id}/remettre`)}
            >
              Voir les ventes à remettre — {eur(reversement.soldeCentimes)}
            </button>

            {reversement.attenteCentimes > 0 && (
              <button
                type="button"
                className="btn btn-secondaire"
                onClick={() => naviguer(`/deposants/${deposant.id}/remettre`)}
                title="Voir aussi les ventes qui ne sont pas encore échues"
              >
                Voir avec l’avance — {eur(reversement.totalDuCentimes)}
              </button>
            )}
          </div>
        </div>
      </div>
    </Ecran>
  )
}
